import { useApolloClient, useMutation } from '@apollo/client/react'
import { type PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { GetCurrentUserDocument, UserLoginAdminDocument } from '../gql/graphql'
import HomePage from '../HomePage'
import { type SiteInfo, SiteProvider } from '../hooks'

/**
 * Renders the children only after ensuring the user is logged in.
 *
 * If no saved login information is found, it shows a QR code login dialog.
 */
export default function LoginRedirector({ children }: PropsWithChildren) {
  const apolloClient = useApolloClient()
  const [state, setState] = useState<string>()
  const [sites, setSites] = useState<SiteInfo[]>([])
  const [login] = useMutation(UserLoginAdminDocument)

  useEffect(() => {
    const authToken = localStorage.getItem('authToken')

    if (authToken) {
      ;(async () => {
        try {
          const { data } = await login()

          if (
            !data?.loginAdmin ||
            data.loginAdmin.__typename === 'GraphqlRequestError'
          ) {
            const errorMsg =
              data?.loginAdmin?.__typename === 'GraphqlRequestError'
                ? data.loginAdmin.message
                : 'No data received'
            console.error('Login failed:', errorMsg)
            localStorage.removeItem('authToken')
            setState('qrlogin')
            return
          }

          const loginResponse = data.loginAdmin
          localStorage.setItem('authToken', loginResponse.authToken)
          setSites(loginResponse.user.sites)

          apolloClient.writeQuery({
            query: GetCurrentUserDocument,
            data: {
              __typename: 'Query',
              // biome-ignore lint/suspicious/noExplicitAny: writeQuery type doesn't accept SiteAdmin
              currentUser: loginResponse.user as any,
            },
          })
          setState('ready')
        } catch (error) {
          console.error('Login error:', error)
          localStorage.removeItem('authToken')
          setState('qrlogin')
        }
      })()
    } else {
      setState('qrlogin')
    }
  }, [login, apolloClient])

  const onLoginSuccess = useCallback((sites: SiteInfo[]) => {
    setSites(sites)
    setState('ready')
  }, [])

  switch (state) {
    case 'qrlogin':
      return <HomePage onLoginSuccess={onLoginSuccess} />
    case 'ready':
      return <SiteProvider sites={sites}>{children}</SiteProvider>
    case 'error':
      return <div>无法登陆</div>
    case undefined:
      return null
    default:
      throw new Error('Invalid LoginRedirector state')
  }
}
